import { UserPlus } from 'lucide-react';
import { useDeferredValue, useState, type FormEvent } from 'react';
import { Link } from 'react-router';
import {
  Badge,
  Button,
  Card,
  ChipToggle,
  ErrorState,
  Field,
  Input,
  Select,
  useToast,
} from '@/components/ui';
import { useBranch } from '@/features/branch/branch-context';
import { useDuplicateCheck, usePayers } from '@/features/patients/api';
import { useQueueActions, useServices } from '@/features/queue/api';
import { EkycSteps } from '@/pages/queue/CounterEkycDrawer';
import { COUNTRY_OPTIONS } from '@/features/patients/countries';
import {
  formatMyKadInput,
  isCompleteMyKad,
  isPlausiblePassport,
  mykadDigits,
  normalizePassportInput,
} from '@/features/patients/identity';
import {
  ASSISTANCE_LABELS,
  CONSENT_LABELS,
  COUNTER_CONSENTS,
  type AssistanceNeed,
  type ConsentType,
  type IdentityType,
  type PatientSummary,
} from '@/features/patients/types';
import { PageContainer } from '@/layouts/PageContainer';
import { isApiError } from '@/lib/api';
import styles from './patients.module.css';

const CONSENTS: ConsentType[] = COUNTER_CONSENTS;
const NEEDS = Object.keys(ASSISTANCE_LABELS) as AssistanceNeed[];

/**
 * Admin v2 "Register patient" (#23): MyKad or passport, nationality, name,
 * phone, service, payer and consents, in the app's order. "Add to walk-in
 * queue" creates the patient if new and adds a walk-in ticket; an IC that
 * exists already adds the visit to that record, never a duplicate.
 */
export function RegisterPatientPage() {
  const { selected: branch } = useBranch();
  const toast = useToast();
  const { registerWalkIn: create } = useQueueActions();
  const { data: services } = useServices();
  const { data: payers } = usePayers();
  const [service, setService] = useState('');
  const [payer, setPayer] = useState('');
  const [registered, setRegistered] = useState<{
    id: string;
    fullName: string;
    identity: PatientSummary['identity'];
    verified: boolean;
  } | null>(null);

  const [docType, setDocType] = useState<IdentityType>('mykad');
  const [docNumber, setDocNumber] = useState('');
  const [nationality, setNationality] = useState('MY');
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [consents, setConsents] = useState<ConsentType[]>([]);
  const [needs, setNeeds] = useState<AssistanceNeed[]>([]);
  const [conflict, setConflict] = useState<{
    id: string;
    fullName: string;
  } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const identityReady =
    docType === 'mykad'
      ? isCompleteMyKad(docNumber)
      : isPlausiblePassport(docNumber) && !!nationality;
  const deferredName = useDeferredValue(fullName.trim());
  const dup = useDuplicateCheck({
    ...(identityReady
      ? {
          identityType: docType,
          identityValue:
            docType === 'mykad'
              ? mykadDigits(docNumber)
              : normalizePassportInput(docNumber),
          ...(docType === 'passport' ? { issuingCountry: nationality } : {}),
        }
      : {}),
    ...(deferredName.length >= 3 ? { fullName: deferredName } : {}),
  });
  const existing: Pick<PatientSummary, 'id' | 'fullName'> | null =
    conflict ?? dup.data?.identityMatch ?? null;
  const possible = (dup.data?.possibleMatches ?? []).filter(
    (p) => p.id !== existing?.id,
  );

  function switchDoc(next: IdentityType) {
    setDocType(next);
    setDocNumber('');
    setConflict(null);
    setNationality(next === 'mykad' ? 'MY' : '');
  }

  async function submitRegistration() {
    setError(null);
    setFieldErrors({});
    try {
      const r = await create.mutateAsync({
        identity: { type: docType, value: docNumber },
        nationality: nationality || undefined,
        fullName,
        phone: phone || undefined,
        consents: Object.fromEntries(consents.map((c) => [c, true])),
        assistanceNeeds: needs,
        serviceId: service,
        payerId: payer || null,
      });
      toast.show({
        tone: 'success',
        title: `Ticket ${r.ticket.number} · ${r.patient.fullName}`,
        body: r.existing
          ? 'Existing record: the visit was added to it, never a duplicate.'
          : `New patient registered at ${branch?.name ?? ''}.`,
      });
      setRegistered({
        id: r.patient.id,
        fullName: r.patient.fullName,
        identity: r.patient.identity,
        verified: r.patient.ekyc.status === 'verified',
      });
      setDocNumber('');
      setFullName('');
      setPhone('');
      setConsents([]);
      setNeeds([]);
      setService('');
      setPayer('');
    } catch (err) {
      if (isApiError(err) && err.code === 'PATIENT_IDENTITY_EXISTS') {
        setConflict(
          (err.problem?.existingPatient as
            { id: string; fullName: string } | undefined) ?? null,
        );
        return;
      }
      if (isApiError(err)) {
        setFieldErrors(
          Object.fromEntries(err.fieldErrors.map((f) => [f.field, f.message])),
        );
      }
      setError(err);
    }
  }

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    void submitRegistration();
  }

  // An existing record only needs the service: the visit is added to it.
  const canSubmit =
    !!branch &&
    identityReady &&
    !!service &&
    (!!existing || fullName.trim().length >= 2) &&
    !create.isPending;
  const ekycPatient = registered ?? null;

  return (
    <PageContainer>
      <div className={styles.registerGrid}>
        <Card
          title="Walk-in registration"
          actions={branch ? <Badge>{branch.name}</Badge> : undefined}
        >
          <form className={styles.form} onSubmit={onSubmit} noValidate>
            <p className={styles.infoNote}>
              Same fields, same order as the app. If the IC already exists, the
              visit is added to that record, never a duplicate.
            </p>
            {!branch && (
              <p className={styles.warnNote} role="alert">
                Choose Bachang or Sungai Udang in the branch switch: a
                registration is recorded at one branch.
              </p>
            )}
            <div className={styles.section}>
              <p className="eyebrow">Identity document</p>
              <ChipToggle
                label="Identity document"
                single
                options={[
                  { value: 'mykad', label: 'MyKad' },
                  { value: 'passport', label: 'Passport' },
                ]}
                selected={[docType]}
                onChange={([next]) => switchDoc(next)}
              />
            </div>
            <Field
              label="MyKad or passport number"
              error={fieldErrors['identity.value']}
            >
              <Input
                value={docNumber}
                onChange={(e) => {
                  setConflict(null);
                  setDocNumber(
                    docType === 'mykad'
                      ? formatMyKadInput(e.target.value)
                      : e.target.value.toUpperCase(),
                  );
                }}
                placeholder={
                  docType === 'mykad' ? 'YYMMDD-PB-####' : 'Passport number'
                }
                inputMode={docType === 'mykad' ? 'numeric' : 'text'}
                autoComplete="off"
              />
            </Field>
            <Field
              label="Nationality"
              hint="Non-Malaysians pay service tax on their bill. Foreign workers for FOMEMA use their passport."
              error={fieldErrors.nationality}
            >
              <Select
                value={nationality}
                onChange={(e) => setNationality(e.target.value)}
                options={COUNTRY_OPTIONS}
                placeholder={
                  docType === 'passport' ? 'Choose nationality' : undefined
                }
              />
            </Field>

            {existing && (
              <div className={styles.existingNote} role="status">
                Existing record found: <strong>{existing.fullName}</strong>. The
                visit is added to this record, never a duplicate.{' '}
                <Link to={`/patients/${existing.id}`}>Open record</Link>
              </div>
            )}
            {!existing && possible.length > 0 && (
              <div className={styles.warnNote} role="status">
                Possible duplicate: {possible.map((p) => p.fullName).join(', ')}{' '}
                (same name or phone). Check before registering; records are
                merged after review, never deleted.
              </div>
            )}

            <Field label="Full name" error={fieldErrors.fullName}>
              <Input
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                autoComplete="off"
              />
            </Field>
            <Field label="Phone" error={fieldErrors.phone}>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+60 12-345 6789"
                inputMode="tel"
              />
            </Field>
            <Field label="Service">
              <Select
                value={service}
                onChange={(e) => setService(e.target.value)}
                placeholder="Choose the service"
                options={(services ?? []).map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
              />
            </Field>
            <Field label="Payment method">
              <Select
                value={payer}
                onChange={(e) => setPayer(e.target.value)}
                options={[
                  { value: '', label: 'Self-pay' },
                  ...(payers ?? []).map((p) => ({
                    value: p.id,
                    label: p.name,
                  })),
                ]}
              />
            </Field>
            <div className={styles.section}>
              <p className="eyebrow">Consents</p>
              <ChipToggle
                label="Consents"
                options={CONSENTS.map((c) => ({
                  value: c,
                  label: CONSENT_LABELS[c],
                }))}
                selected={consents}
                onChange={setConsents}
              />
              <p className={styles.note}>
                Marketing is kept separate: health promotions only go to
                patients who opt in.
              </p>
            </div>
            <div className={styles.section}>
              <p className="eyebrow">Assistance needs</p>
              <ChipToggle
                label="Assistance needs"
                options={NEEDS.map((n) => ({
                  value: n,
                  label: ASSISTANCE_LABELS[n],
                }))}
                selected={needs}
                onChange={setNeeds}
              />
            </div>
            {error !== null && (
              <ErrorState
                error={error}
                onRetry={() => void submitRegistration()}
              />
            )}
            <Button
              type="submit"
              variant="primary"
              icon={<UserPlus size={16} />}
              loading={create.isPending}
              disabled={!canSubmit}
            >
              Add to walk-in queue
            </Button>
          </form>
        </Card>
        <Card title="Counter eKYC">
          <div className={styles.drawer}>
            <p className={styles.note}>
              For patients whose in-app scan failed, or who have no phone. Same
              provider, same result written to the same record.
            </p>
            {ekycPatient?.verified ? (
              <p className={styles.okNote}>
                {ekycPatient.fullName} is eKYC-verified.
              </p>
            ) : (
              <EkycSteps
                key={ekycPatient?.id ?? 'none'}
                patient={ekycPatient}
                onVerified={() =>
                  setRegistered((r) => (r ? { ...r, verified: true } : r))
                }
              />
            )}
            {!ekycPatient && (
              <p className={styles.sub}>
                Add the patient to the queue first; the steps then apply to that
                record.
              </p>
            )}
            {ekycPatient && (
              <Link to={`/patients/${ekycPatient.id}`}>
                Open {ekycPatient.fullName}’s record
              </Link>
            )}
          </div>
        </Card>
      </div>
    </PageContainer>
  );
}
